import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import api from "../services/api";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Start true to check session
  const [sessionExpired, setSessionExpired] = useState(false);

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    localStorage.removeItem("wascaner_session");
    localStorage.removeItem("token");
    setSessionExpired(true);
  }, []);

  // Expose handleSessionExpired globally for axios interceptor
  useEffect(() => {
    window.__handleSessionExpired = handleSessionExpired;
    return () => {
      delete window.__handleSessionExpired;
    };
  }, [handleSessionExpired]);

  useEffect(() => {
    const validateSession = async () => {
      const token = localStorage.getItem("token");
      const raw = localStorage.getItem("wascaner_session");

      if (!token || !raw) {
        setLoading(false);
        return;
      }

      try {
        // Validate token by fetching user from backend
        // We fetch user first, and only if successful we fetch subscription
        // to avoid multiple 401 logs if the token is expired/invalid.
        const userData = await api.getUser();

        let subscriptionData = null;
        try {
          subscriptionData = await api.getSubscription();
        } catch (subErr) {
          console.warn(
            "Could not fetch subscription data during validation",
            subErr?.message
          );
        }

        const normalizedSubscription =
          subscriptionData?.subscription &&
          typeof subscriptionData.subscription === "object"
            ? subscriptionData.subscription
            : subscriptionData;

        const normalized = normalizeUser(userData);
        const merged = {
          ...normalized,
          ...(normalizedSubscription || {}),
          subscription_plan:
            normalizedSubscription?.subscription_plan ??
            normalized.subscription_plan,
          subscription_status:
            normalizedSubscription?.subscription_status ??
            normalized.subscription_status,
        };
        setUser(merged);
        localStorage.setItem("wascaner_session", JSON.stringify(merged));
      } catch (error) {
        // Token invalid or expired
        console.error("Session validation failed:", error);
        if (error.response?.status === 401 || error.response?.status === 403) {
          handleSessionExpired();
        } else {
          // Network error - use cached user but mark as potentially stale
          try {
            const parsed = JSON.parse(raw);
            setUser(normalizeUser(parsed));
          } catch (parseErr) {
            setUser(null);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    validateSession();
  }, [handleSessionExpired]);

  const normalizeUser = (u) => {
    if (!u) return u;
    const normalized = { ...u };
    // ensure phones array exists; support older key phoneNumbers
    normalized.phones = Array.isArray(u.phones)
      ? u.phones
      : Array.isArray(u.phoneNumbers)
      ? u.phoneNumbers
      : [];
    normalized.phoneNumbers = Array.isArray(normalized.phoneNumbers)
      ? normalized.phoneNumbers
      : normalized.phones;
    if (!normalized.phone && normalized.phones && normalized.phones.length > 0)
      normalized.phone = normalized.phones[0];
    return normalized;
  };

  const login = async (email, password) => {
    try {
      const response = await api.login(email, password);
      console.log("AuthContext.login: response", {
        hasToken: !!response?.token,
        user: response?.user
          ? {
              user_id: response.user.user_id || response.user.id || null,
              force_password_change: !!response.user.force_password_change,
            }
          : null,
      });
      if (response.token) {
        localStorage.setItem("token", response.token);
        const normalized = normalizeUser(response.user);
        localStorage.setItem("wascaner_session", JSON.stringify(normalized));
        setUser(normalized);
        // Refresh from backend to pick any computed flags (e.g., phone_required)
        try {
          const refreshed = await api.getUser();
          const normalizedRefreshed = normalizeUser(refreshed);
          setUser(normalizedRefreshed);
          localStorage.setItem(
            "wascaner_session",
            JSON.stringify(normalizedRefreshed)
          );
        } catch (e) {
          console.warn("AuthContext.login: could not refresh user", e?.message);
        }
        console.log("AuthContext.login: stored session", {
          tokenPresent: !!localStorage.getItem("token"),
          session: JSON.parse(
            localStorage.getItem("wascaner_session") || "null"
          ),
        });
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.getUser();
      const normalized = normalizeUser(userData);
      localStorage.setItem("wascaner_session", JSON.stringify(normalized));
      setUser(normalized);
      return normalized;
    } catch (error) {
      console.error("Failed to refresh user:", error);
      throw error;
    }
  }, []);

  const signup = async (userData) => {
    // Don't set global loading for signup - it would cause App to show spinner
    // and reset the SignUp component state
    try {
      const response = await api.signup(userData);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const verifyCode = async ({ user_id, code }) => {
    try {
      const response = await api.verifyCode({ user_id, code });
      if (response.success && response.token) {
        localStorage.setItem("token", response.token);
        const normalized = normalizeUser(response.user);
        localStorage.setItem("wascaner_session", JSON.stringify(normalized));
        setUser(normalized);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("wascaner_session");
    localStorage.removeItem("token");
  };

  const refresh = async () => {
    const userData = await api.getUser();
    const subscriptionData = await api.getSubscription().catch(() => null);

    const normalizedSubscription =
      subscriptionData?.subscription &&
      typeof subscriptionData.subscription === "object"
        ? subscriptionData.subscription
        : subscriptionData;
    const normalized = normalizeUser(userData);
    const merged = {
      ...normalized,
      ...(normalizedSubscription || {}),
      subscription_plan:
        normalizedSubscription?.subscription_plan ??
        normalized.subscription_plan,
      subscription_status:
        normalizedSubscription?.subscription_status ??
        normalized.subscription_status,
    };
    setUser(merged);
    localStorage.setItem("wascaner_session", JSON.stringify(merged));
    return merged;
  };

  const updateUserData = (userData) => {
    const normalized = normalizeUser(userData);
    setUser(normalized);
    localStorage.setItem("wascaner_session", JSON.stringify(normalized));
  };

  const clearSessionExpired = () => {
    setSessionExpired(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        sessionExpired,
        login,
        logout,
        refresh,
        refreshUser,
        setUser,
        signup,
        verifyCode,
        updateUserData,
        clearSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
